import { useOutletContext, useParams } from "react-router-dom";
import { SimpleCard, SpinnerDefault, TitlelessCard } from "../../../components";
import { useEffect, useState } from "react";
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartSeries,
  ChartSeriesItem,
  ChartValueAxis,
  ChartValueAxisItem,
} from "@progress/kendo-react-charts";
import InspectionFlwBaselineService from "../../../services/InspectionFlwBaselineService";
import { InspectionFlwBaselineModel } from "../../../data/entities";
import { InspectionsPageContext } from "../InspectionsPage";
import { useAppSelector } from "../../../app/hooks";

interface EditState {
  position: string;
  wallThickness: string;
}

export function InspectionsProcessInspectionBaseline() {
  const context: InspectionsPageContext = useOutletContext();
  context.setTitle("Inspection Baseline");

  const params = useParams();
  const id = parseInt(params["id"] ?? "0");

  const tubingLength = useAppSelector(
    (state: any) => state.inspectionManagement.inspection?.tubing?.length ?? 0
  );

  const [controlPoints, setControlPoints] = useState<InspectionFlwBaselineModel[]>([]);
  const [chartPoints, setChartPoints] = useState<{ position: number; wallThickness: number }[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editState, setEditState] = useState<EditState>({ position: "", wallThickness: "" });

  const [isAdding, setIsAdding] = useState(false);
  const [newState, setNewState] = useState<EditState>({ position: "", wallThickness: "" });

  const [validationError, setValidationError] = useState<string | null>(null);

  const loadData = async () => {
    const data = await new InspectionFlwBaselineService().getControlPoints(id);
    const sorted = data ? [...data].sort((a, b) => a.position - b.position) : [];
    setControlPoints(sorted);
    if (sorted.length > 0) {
      const chart = await new InspectionFlwBaselineService().getFlwBaselineChart(id);
      setChartPoints(chart ?? []);
    } else {
      setChartPoints([]);
    }
    setIsDataLoaded(true);
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const computeSortIndex = (position: number, excludeId?: number): number => {
    const others = controlPoints.filter((p) => p.id !== excludeId);
    const sorted = [...others, { position } as InspectionFlwBaselineModel]
      .sort((a, b) => a.position - b.position);
    return sorted.findIndex((p) => p.position === position) + 1;
  };

  const validate = (position: number, wallThickness: number, excludeId?: number): string | null => {
    if (isNaN(position) || position < 0) return "Position must be a valid non-negative number.";
    if (tubingLength > 0 && position > tubingLength)
      return `Position must be within the tubing length (0 – ${tubingLength} m).`;
    if (isNaN(wallThickness) || wallThickness <= 0) return "Wall thickness must be a positive number.";
    const duplicate = controlPoints.find(
      (p) => p.position === position && p.id !== excludeId
    );
    if (duplicate) return `A control point at position ${position} already exists.`;
    return null;
  };

  const startEdit = (point: InspectionFlwBaselineModel) => {
    setValidationError(null);
    setIsAdding(false);
    setEditingId(point.id);
    setEditState({ position: String(point.position), wallThickness: String(point.wallThickness) });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setValidationError(null);
  };

  const saveEdit = async () => {
    const position = parseFloat(editState.position);
    const wallThickness = parseFloat(editState.wallThickness);
    const error = validate(position, wallThickness, editingId!);
    if (error) { setValidationError(error); return; }

    setShowSpinner(true);
    setValidationError(null);
    const sortIndex = computeSortIndex(position, editingId!);
    const ok = await new InspectionFlwBaselineService().updateControlPoint(id, editingId!, { position, wallThickness, sortIndex });
    if (ok) {
      setEditingId(null);
      await loadData();
    }
    setShowSpinner(false);
  };

  const deletePoint = async (pointId: number) => {
    setShowSpinner(true);
    await new InspectionFlwBaselineService().deleteControlPoint(id, pointId);
    await loadData();
    setShowSpinner(false);
  };

  const startAdd = () => {
    setValidationError(null);
    setEditingId(null);
    setNewState({ position: "", wallThickness: "" });
    setIsAdding(true);
  };

  const cancelAdd = () => {
    setIsAdding(false);
    setValidationError(null);
  };

  const saveAdd = async () => {
    const position = parseFloat(newState.position);
    const wallThickness = parseFloat(newState.wallThickness);
    const error = validate(position, wallThickness);
    if (error) { setValidationError(error); return; }

    setShowSpinner(true);
    setValidationError(null);
    const sortIndex = computeSortIndex(position);
    const created = await new InspectionFlwBaselineService().addControlPoint(id, { position, wallThickness, sortIndex });
    if (created) {
      setNewState({ position: "", wallThickness: "" });
      await loadData();
    }
    setShowSpinner(false);
  };


  if (!isDataLoaded) return <SpinnerDefault />;

  return (
    <>
      <SimpleCard className="mb-3 p-0" title="Inspection-Level Baseline Override">
        <p>
          Define per-inspection baseline control points (position and wall thickness) that
          override the tubing-level FLW baseline. Positions must be unique and non-negative.
        </p>

        {validationError && (
          <div className="alert alert-warning mb-3" role="alert">
            {validationError}
          </div>
        )}

        <SpinnerDefault show={showSpinner} fullscreen={true} />

        <table className="table table-hover mt-2">
          <thead>
            <tr>
              <th>Position (m)</th>
              <th>Wall Thickness</th>
              <th style={{ width: "160px" }}></th>
            </tr>
          </thead>
          <tbody>
            {controlPoints.map((point) =>
              editingId === point.id ? (
                <tr key={point.id}>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editState.position}
                      min={0}
                      step="any"
                      onChange={(e) =>
                        setEditState((s) => ({ ...s, position: e.target.value }))
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editState.wallThickness}
                      min={0}
                      step="any"
                      onChange={(e) =>
                        setEditState((s) => ({ ...s, wallThickness: e.target.value }))
                      }
                    />
                  </td>
                  <td>
                    <button className="btn btn-sm btn-primary me-1" onClick={saveEdit}>
                      Save
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={cancelEdit}>
                      Cancel
                    </button>
                  </td>
                </tr>
              ) : (
                <tr key={point.id}>
                  <td>{point.position}</td>
                  <td>{point.wallThickness}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-1"
                      onClick={() => startEdit(point)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deletePoint(point.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              )
            )}

            {isAdding && (
              <tr>
                <td>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="0"
                    value={newState.position}
                    min={0}
                    step="any"
                    autoFocus
                    onChange={(e) =>
                      setNewState((s) => ({ ...s, position: e.target.value }))
                    }
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="0.0000"
                    value={newState.wallThickness}
                    min={0}
                    step="any"
                    onChange={(e) =>
                      setNewState((s) => ({ ...s, wallThickness: e.target.value }))
                    }
                  />
                </td>
                <td>
                  <button className="btn btn-sm btn-success me-1" onClick={saveAdd}>
                    Add
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={cancelAdd}>
                    Cancel
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {!isAdding && editingId === null && (
          <div className="mb-3">
            <button className="btn btn-outline-primary" onClick={startAdd}>
              + Add Control Point
            </button>
          </div>
        )}

      </SimpleCard>

      {chartPoints.length > 0 && (
        <TitlelessCard className="mb-3">
          <Chart className="chart">
            <ChartCategoryAxis>
              <ChartCategoryAxisItem
                title={{ text: "Position (m)" }}
                categories={chartPoints.map((p) => p.position)}
                majorGridLines={{ visible: false }}
                majorTicks={{ visible: true, step: Math.round(chartPoints.length / 10 / 10) * 10 }}
                labels={{ step: Math.round(chartPoints.length / 10 / 10) * 10, format: "{0:N0}" }}
              />
            </ChartCategoryAxis>
            <ChartValueAxis>
              <ChartValueAxisItem
                title={{ text: "Wall Thickness" }}
                labels={{ visible: true, format: "{0:N4}" }}
                majorGridLines={{ visible: true }}
                minorGridLines={{ visible: false }}
                majorTicks={{ visible: false }}
                minorTicks={{ visible: false }}
                line={{ visible: false }}
              />
            </ChartValueAxis>
            <ChartSeries>
              <ChartSeriesItem
                name="Wall Thickness"
                data={chartPoints.map((p) => p.wallThickness)}
                type="line"
                color="#0D698B"
                width={1.5}
                markers={{ visible: false }}
              />
            </ChartSeries>
          </Chart>
        </TitlelessCard>
      )}
    </>
  );
}

export default InspectionsProcessInspectionBaseline;
